/**
 * JWT Token and API Request Mixin
 * Provides common JWT token management and API request functionality
 */
export default {
    data() {
        return {
            jwt_token: null,
            jwt_token_promise: null,
        };
    },

    methods: {
        /**
         * Get stored JWT token from localStorage with expiry check
         */
        getStoredJwtToken() {
            try {
                const storedTokenData = localStorage.getItem('jwt_token_data');
                if (!storedTokenData) return null;

                const tokenData = JSON.parse(storedTokenData);
                const now = Date.now();

                // Check if token is still valid (not expired)
                if (tokenData.expiry && now < tokenData.expiry) {
                    return tokenData.token;
                }

                // Token expired, remove from localStorage
                this.removeStoredJwtToken();
                return null;
            } catch (error) {
                // Invalid JSON, remove from localStorage
                this.removeStoredJwtToken();
                return null;
            }
        },

        /**
         * Store JWT token in localStorage with expiry timestamp
         */
        storeJwtToken(token) {
            const tokenData = {
                token: token,
                expiry: Date.now() + (55 * 60 * 1000) // 55 minutes in milliseconds
            };
            localStorage.setItem('jwt_token_data', JSON.stringify(tokenData));
        },

        /**
         * Remove JWT token from localStorage
         */
        removeStoredJwtToken() {
            localStorage.removeItem('jwt_token_data');
            // Also remove legacy format for backward compatibility
            localStorage.removeItem('jwt_token');
            localStorage.removeItem('jwt_token_timestamp');
        },

        /**
         * Get JWT token - checks memory cache, localStorage, or fetches new token
         */
        async getJwtToken() {
            // Return existing promise if already fetching
            if (this.jwt_token_promise) {
                return this.jwt_token_promise;
            }

            // Return cached token if available in memory
            if (this.jwt_token) {
                return this.jwt_token;
            }

            // Check localStorage for valid token
            const storedToken = this.getStoredJwtToken();
            if (storedToken) {
                this.jwt_token = storedToken;
                return this.jwt_token;
            }

            // Fetch JWT token
            this.jwt_token_promise = axios.get('/api/internal/users/exchange.json')
                .then(response => {
                    if (response.data && response.data.token) {
                        this.jwt_token = response.data.token;
                        this.storeJwtToken(this.jwt_token);
                        return this.jwt_token;
                    }
                    throw new Error('No token in response');
                })
                .catch(error => {
                    console.error('Failed to get JWT token:', error);
                    this.jwt_token = null;
                    this.removeStoredJwtToken();
                    throw error;
                })
                .finally(() => {
                    this.jwt_token_promise = null;
                });

            return this.jwt_token_promise;
        },

        /**
         * Make API request with JWT token handling and retry logic
         * @param {Function} requestFn - Function that takes Bearer token and makes the request
         */
        async makeApiRequest(requestFn) {
            try {
                const jwtToken = await this.getJwtToken();
                return await requestFn(`Bearer ${jwtToken}`);
            } catch (error) {
                // Check if it's a JWT-related error
                const isJwtError = error.response &&
                    (error.response.status === 401 ||
                     (error.response.data &&
                      ((error.response.data.message && error.response.data.message.includes('JWT')) ||
                       (error.response.data.data && error.response.data.data.message && error.response.data.data.message.includes('JWT')))));

                if (isJwtError) {
                    console.log('JWT token expired or invalid, retrying with fresh token...');
                    // Clear cached token and localStorage, then fetch fresh one
                    this.jwt_token = null;
                    this.jwt_token_promise = null;
                    this.removeStoredJwtToken();
                    const freshToken = await this.getJwtToken();
                    return await requestFn(`Bearer ${freshToken}`);
                }

                // If not a JWT error, throw the original error
                throw error;
            }
        },
    },
};


// WEBPACK FOOTER //
// ./src/Assets/mixins/ApiAuthMixin.js